import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { MapPin, Clock, UserCheck, Zap } from 'lucide-react';
import { useActiveOffers } from '../hooks/useActiveOffers';
import { useDrivers } from '../hooks/useDrivers';
import type { ActiveOffer } from '../lib/types';

function Countdown({ expiresAt }: { expiresAt: string | null }) {
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    if (!expiresAt) return;

    const tick = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) { setRemaining('Expired'); return; }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(`${m}:${String(s).padStart(2, '0')}`);
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const isUrgent = expiresAt && new Date(expiresAt).getTime() - Date.now() < 120000;

  return (
    <span className={`inline-flex items-center gap-1 text-xs font-mono font-bold ${isUrgent ? 'text-red-500' : 'text-amber-500'}`}>
      <Clock className="w-3 h-3" />
      {remaining || '—'}
    </span>
  );
}

function AssignModal({
  offer,
  onAssign,
  onClose,
}: {
  offer: ActiveOffer;
  onAssign: (driverId: string) => Promise<void>;
  onClose: () => void;
}) {
  const { drivers, loading } = useDrivers({ status: 'active', perPage: 50 });
  const [selectedDriver, setSelectedDriver] = useState('');
  const [assigning, setAssigning] = useState(false);

  const handleAssign = async () => {
    if (!selectedDriver) return;
    setAssigning(true);
    await onAssign(selectedDriver);
    setAssigning(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
            Assign Offer to Driver
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            {offer.origin_address ?? 'Origin'} → {offer.dest_address ?? 'Destination'}
          </p>
        </div>
        <div className="px-5 py-4">
          {loading
            ? <div className="skeleton h-10 rounded-xl" />
            : (
              <select
                value={selectedDriver}
                onChange={(e) => setSelectedDriver(e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-electric-400"
              >
                <option value="">Select active driver...</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>{d.full_name}</option>
                ))}
              </select>
            )
          }
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedDriver || assigning}
            className="btn-primary px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {assigning ? (
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <><UserCheck className="w-4 h-4" /> Assign</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

const ActiveOffers: React.FC = () => {
  const { offers, loading, error, assignOffer } = useActiveOffers();
  const [assigningOffer, setAssigningOffer] = useState<ActiveOffer | null>(null);

  const handleAssign = async (driverId: string) => {
    if (!assigningOffer) return;
    await assignOffer(assigningOffer.id, driverId);
  };

  return (
    <Layout
      title="Active Offers"
      breadcrumbs={[{ label: 'Home', path: '/dashboard' }, { label: 'Active Offers' }]}
    >
      {assigningOffer && (
        <AssignModal
          offer={assigningOffer}
          onAssign={handleAssign}
          onClose={() => setAssigningOffer(null)}
        />
      )}

      <div className="space-y-4">
        {/* Header */}
        <div className="card px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="live-dot" />
            <span className="font-semibold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
              Live Offers
            </span>
          </div>
          <span className="text-sm text-slate-500">
            <span className="font-medium text-slate-700">{offers.length}</span> open
          </span>
        </div>

        {/* Error */}
        {error && (
          <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Cards grid */}
        {loading
          ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="card p-5 space-y-3">
                  <div className="skeleton h-4 rounded w-3/4" />
                  <div className="skeleton h-4 rounded w-1/2" />
                  <div className="skeleton h-8 rounded-xl w-full" />
                </div>
              ))}
            </div>
          )
          : offers.length === 0
            ? (
              <div className="card flex flex-col items-center justify-center py-20 text-center">
                <Zap className="w-10 h-10 text-slate-200 mb-3" />
                <p className="text-slate-400 text-sm">No open offers right now</p>
                <p className="text-slate-300 text-xs mt-1">New offers will appear here in real time</p>
              </div>
            )
            : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {offers.map((offer) => (
                  <div key={offer.id} className="card p-5 hover:-translate-y-0.5 transition-all duration-200">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-0.5">
                          <MapPin className="w-3 h-3 text-emerald-400" />
                          <span className="truncate">{offer.origin_address ?? 'Unknown origin'}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-400">
                          <MapPin className="w-3 h-3 text-red-400" />
                          <span className="truncate">{offer.dest_address ?? 'Unknown destination'}</span>
                        </div>
                      </div>
                      <Countdown expiresAt={offer.expires_at} />
                    </div>

                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
                        {offer.estimated_fare != null ? `$${offer.estimated_fare.toFixed(2)}` : '—'}
                      </span>
                      <span className="text-xs text-slate-400">
                        {new Date(offer.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    <button
                      onClick={() => setAssigningOffer(offer)}
                      className="btn-primary w-full py-2 text-sm"
                    >
                      <UserCheck className="w-4 h-4" />
                      Assign Driver
                    </button>
                  </div>
                ))}
              </div>
            )
        }
      </div>
    </Layout>
  );
};

export default ActiveOffers;
